(() => {
  "use strict";

  window.BYL = window.BYL || {};

  const CONFIG = window.BYL_CONFIG || {};

  const API_BASE = String(CONFIG.apiBase || "").replace(/\/$/, "");

  const SCHOOL_STORAGE_KEY = "byl_verified_schools_v3";
  const SCHOOL_DEFAULT_KEY = "byl_default_school_v1";

  const variantCache = new Map();


  /* =========================================================
     ERROR TYPE
     ========================================================= */

  class BYLServiceError extends Error {
    constructor(message, options = {}) {
      super(message);

      this.name = "BYLServiceError";
      this.status = options.status || 0;
      this.code = options.code || "";
      this.data = options.data || null;
    }
  }


  /* =========================================================
     GENERAL FETCH HELPER
     ========================================================= */

  async function fetchJson(url, options = {}) {
    let response;

    try {
      response = await fetch(url, options);
    } catch (error) {
      console.error("BYL network error:", error);

      throw new BYLServiceError(
        "We couldn't connect to the ordering service. Please check your connection and try again.",
        {
          code: "NETWORK_ERROR"
        }
      );
    }

    let data = null;

    try {
      data = await response.json();
    } catch {
      data = null;
    }

    if (!response.ok) {
      const message =
        data?.error ||
        data?.description ||
        data?.message ||
        `Request failed with status ${response.status}.`;

      throw new BYLServiceError(
        message,
        {
          status: response.status,
          code: "HTTP_ERROR",
          data
        }
      );
    }

    return data;
  }


  /* =========================================================
     SCHOOL TOKEN STORAGE
     ========================================================= */

  function readStoredSchools() {
    try {
      const raw =
        localStorage.getItem(
          SCHOOL_STORAGE_KEY
        );

      if (!raw) {
        return [];
      }

      const parsed =
        JSON.parse(raw);

      if (!Array.isArray(parsed)) {
        return [];
      }

      return parsed
        .filter(entry =>
          entry &&
          entry.schoolId &&
          entry.accessToken
        )
        .map(entry => ({
          schoolId:
            String(entry.schoolId),

          schoolName:
            String(
              entry.schoolName || ""
            ),

          accessToken:
            String(entry.accessToken)
        }));

    } catch (error) {
      console.warn(
        "BYL: Could not read stored schools.",
        error
      );

      return [];
    }
  }


  function writeStoredSchools(entries) {
    const clean =
      Array.isArray(entries)
        ? entries
            .filter(entry =>
              entry &&
              entry.schoolId &&
              entry.accessToken
            )
            .map(entry => ({
              schoolId:
                String(entry.schoolId),

              schoolName:
                String(
                  entry.schoolName || ""
                ),

              accessToken:
                String(entry.accessToken)
            }))
        : [];

    try {
      localStorage.setItem(
        SCHOOL_STORAGE_KEY,
        JSON.stringify(clean)
      );
    } catch (error) {
      console.warn(
        "BYL: Could not save school access locally.",
        error
      );
    }

    return clean;
  }


  function saveSchoolAccess(
    school,
    accessToken
  ) {
    if (
      !school?.schoolId ||
      !accessToken
    ) {
      return;
    }

    const entries =
      readStoredSchools();

    const existingIndex =
      entries.findIndex(
        entry =>
          entry.schoolId ===
          String(school.schoolId)
      );

    const entry = {
      schoolId:
        String(school.schoolId),

      schoolName:
        String(
          school.schoolName || ""
        ),

      accessToken:
        String(accessToken)
    };

    if (existingIndex >= 0) {
      entries[existingIndex] = entry;
    } else {
      entries.push(entry);
    }

    writeStoredSchools(entries);
  }


  function removeStoredSchool(
    schoolId
  ) {
    const target =
      String(schoolId);

    const next =
      readStoredSchools()
        .filter(
          entry =>
            entry.schoolId !==
            target
        );

    writeStoredSchools(next);

    if (
      getDefaultStoredSchoolId() ===
      target
    ) {
      setDefaultStoredSchoolId(
        next[0]?.schoolId || ""
      );
    }
  }


  function getDefaultStoredSchoolId() {
    try {
      return String(
        localStorage.getItem(
          SCHOOL_DEFAULT_KEY
        ) || ""
      );
    } catch {
      return "";
    }
  }


  function setDefaultStoredSchoolId(
    schoolId
  ) {
    const id =
      String(schoolId || "");

    try {
      if (id) {
        localStorage.setItem(
          SCHOOL_DEFAULT_KEY,
          id
        );
      } else {
        localStorage.removeItem(
          SCHOOL_DEFAULT_KEY
        );
      }
    } catch {
      // no-op
    }
  }


  function prioritizeStoredSchool(
    schoolId
  ) {
    const id =
      String(schoolId || "");

    if (!id) {
      return readStoredSchools();
    }

    setDefaultStoredSchoolId(id);

    const entries =
      readStoredSchools();

    return writeStoredSchools([
      ...entries.filter(
        entry =>
          entry.schoolId === id
      ),
      ...entries.filter(
        entry =>
          entry.schoolId !== id
      )
    ]);
  }


  function clearStoredSchools() {
    try {
      localStorage.removeItem(
        SCHOOL_STORAGE_KEY
      );

      localStorage.removeItem(
        SCHOOL_DEFAULT_KEY
      );
    } catch {
      // no-op
    }
  }


  /* =========================================================
     AWS SCHOOL ACCESS
     ========================================================= */

  function assertApiBase() {
    if (!API_BASE) {
      throw new BYLServiceError(
        "BYL API base URL is not configured.",
        {
          code: "MISSING_API_BASE"
        }
      );
    }
  }


  async function requestSchoolAccess(
    payload
  ) {
    assertApiBase();

    const data =
      await fetchJson(
        `${API_BASE}/school/access`,
        {
          method: "POST",

          headers: {
            "Content-Type":
              "application/json",

            Accept:
              "application/json"
          },

          body:
            JSON.stringify(payload)
        }
      );

    if (
      !data?.success ||
      !data?.school
    ) {
      throw new BYLServiceError(
        data?.error ||
        "School data could not be loaded.",
        {
          code:
            "INVALID_SCHOOL_RESPONSE",

          data
        }
      );
    }

    return data;
  }


  async function unlockSchool(
    code
  ) {
    const cleanCode =
      String(code || "")
        .trim()
        .toUpperCase()
        .replace(/\s+/g, "");

    if (!cleanCode) {
      throw new BYLServiceError(
        "Enter your school code.",
        {
          code: "MISSING_CODE"
        }
      );
    }

    const data =
      await requestSchoolAccess({
        code: cleanCode
      });

    if (data.accessToken) {
      saveSchoolAccess(
        data.school,
        data.accessToken
      );
    }

    return {
      school: data.school,
      accessToken:
        data.accessToken || ""
    };
  }


  async function restoreSchool(
    accessToken
  ) {
    const token =
      String(accessToken || "")
        .trim();

    if (!token) {
      throw new BYLServiceError(
        "School access token is missing.",
        {
          code: "MISSING_ACCESS_TOKEN"
        }
      );
    }

    return requestSchoolAccess({
      accessToken: token
    });
  }


  async function restoreStoredSchools() {
    const saved =
      readStoredSchools();

    if (!saved.length) {
      return [];
    }

    const restored = [];
    const defaultSchoolId =
      getDefaultStoredSchoolId();

    for (const entry of saved) {
      try {
        const data =
          await restoreSchool(
            entry.accessToken
          );

        const result = {
          school:
            data.school,

          accessToken:
            data.accessToken ||
            entry.accessToken
        };

        restored.push(result);

        saveSchoolAccess(
          result.school,
          result.accessToken
        );

      } catch (error) {
        console.warn(
          "BYL: Stored school access was rejected:",
          entry.schoolId,
          error
        );

        removeStoredSchool(
          entry.schoolId
        );
      }
    }

    return defaultSchoolId
      ? [
          ...restored.filter(
            entry =>
              String(
                entry.school?.schoolId || ""
              ) === defaultSchoolId
          ),
          ...restored.filter(
            entry =>
              String(
                entry.school?.schoolId || ""
              ) !== defaultSchoolId
          )
        ]
      : restored;
  }


  /* =========================================================
     SHOPIFY VARIANT DATA
     ========================================================= */

  async function getVariant(
    variantId,
    options = {}
  ) {
    const id =
      String(variantId || "")
        .trim();

    if (!id) {
      throw new BYLServiceError(
        "Variant ID is missing.",
        {
          code:
            "MISSING_VARIANT_ID"
        }
      );
    }

    const forceRefresh =
      options.forceRefresh === true;

    if (
      !forceRefresh &&
      variantCache.has(id)
    ) {
      return variantCache.get(id);
    }

    const promise =
      fetchJson(
        `/variants/${encodeURIComponent(id)}.js`,
        {
          headers: {
            Accept:
              "application/json"
          }
        }
      )
        .then(data => {
          const variant = {
            id:
              String(
                data.id || id
              ),

            title:
              String(
                data.title || ""
              ),

            name:
              String(
                data.name || ""
              ),

            publicTitle:
              String(
                data.public_title || ""
              ),

            price:
              Number.isFinite(
                Number(data.price)
              )
                ? Number(data.price)
                : null,

            available:
              data.available !== false,

            raw:
              data
          };

          variantCache.set(
            id,
            variant
          );

          return variant;
        })
        .catch(error => {
          variantCache.delete(id);
          throw error;
        });

    variantCache.set(
      id,
      promise
    );

    const resolved =
      await promise;

    variantCache.set(
      id,
      resolved
    );

    return resolved;
  }


  async function getVariants(
    variantIds
  ) {
    const ids =
      [...new Set(
        (variantIds || [])
          .map(id =>
            String(id || "")
              .trim()
          )
          .filter(Boolean)
      )];

    const results =
      await Promise.all(
        ids.map(async id => {
          try {
            const variant =
              await getVariant(id);

            return {
              id,
              ok: true,
              variant
            };

          } catch (error) {
            console.warn(
              `BYL: Could not load variant ${id}`,
              error
            );

            return {
              id,
              ok: false,
              error
            };
          }
        })
      );

    return results;
  }


  function peekVariant(
    variantId
  ) {
    const value =
      variantCache.get(
        String(variantId || "")
      );

    if (!value) {
      return null;
    }

    if (
      typeof value?.then ===
      "function"
    ) {
      return null;
    }

    return value;
  }


  function getVariantPrice(
    variantId
  ) {
    const variant =
      peekVariant(variantId);

    return Number.isFinite(
      Number(variant?.price)
    )
      ? Number(variant.price)
      : null;
  }


  function clearVariantCache() {
    variantCache.clear();
  }


  /* =========================================================
     SHOPIFY CART
     ========================================================= */

  function shopifyRoot() {
    const root =
      window.Shopify?.routes?.root ||
      "/";

    return String(root);
  }


  async function addCartItems(
    items
  ) {
    const cleanItems =
      Array.isArray(items)
        ? items.filter(
            item =>
              item &&
              item.id &&
              Number(item.quantity || 1) > 0
          )
        : [];

    if (!cleanItems.length) {
      throw new BYLServiceError(
        "There are no items to add to the cart.",
        {
          code: "EMPTY_CART_ITEMS"
        }
      );
    }

    const itemWithoutCutoff =
      cleanItems.find(item =>
        item?.properties?._BYLServiceDate &&
        !item?.properties?._BYLCutoffAt
      );

    if (itemWithoutCutoff) {
      throw new BYLServiceError(
        "The ordering deadline could not be saved for one of the selected items. Please refresh and try again.",
        {
          code: "MISSING_CUTOFF_SNAPSHOT"
        }
      );
    }

    const root =
      shopifyRoot();

    const data =
      await fetchJson(
        `${root}cart/add.js`,
        {
          method: "POST",

          headers: {
            "Content-Type":
              "application/json",

            Accept:
              "application/json"
          },

          body:
            JSON.stringify({
              items: cleanItems
            })
        }
      );

    return data;
  }


  function cartUrl() {
    return `${shopifyRoot()}cart`;
  }


  function goToCart() {
    window.location.href =
      cartUrl();
  }


  /* =========================================================
     MONEY
     ========================================================= */

  function formatMoney(
    cents
  ) {
    const value =
      Number(cents);

    if (!Number.isFinite(value)) {
      return "";
    }

    return new Intl.NumberFormat(
      "en-CA",
      {
        style: "currency",
        currency: "CAD"
      }
    ).format(value / 100);
  }


  /* =========================================================
     PUBLIC API
     ========================================================= */

  BYL.services = {
    BYLServiceError,

    apiBase:
      API_BASE,

    fetchJson,

    schools: {
      unlock:
        unlockSchool,

      restore:
        restoreSchool,

      restoreStored:
        restoreStoredSchools,

      getStored:
        readStoredSchools,

      saveAccess:
        saveSchoolAccess,

      defaultStored:
        getDefaultStoredSchoolId,

      prioritizeStored:
        prioritizeStoredSchool,

      removeStored:
        removeStoredSchool,

      clearStored:
        clearStoredSchools
    },

    variants: {
      get:
        getVariant,

      getMany:
        getVariants,

      peek:
        peekVariant,

      getPrice:
        getVariantPrice,

      clearCache:
        clearVariantCache
    },

    cart: {
      add:
        addCartItems,

      getUrl:
        cartUrl,

      go:
        goToCart
    },

    money: {
      format:
        formatMoney
    }
  };

})();
